import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, EMPTY, map, of, switchMap } from 'rxjs';
import {
    followActions,
    loadActions,
    showcaseFollowActions,
    showcaseLoadActions,
    showcaseUnfollowActions,
    unfollowActions
} from './follow-creativeset.actions';
import { FollowCreativeSetDataService } from './follow-creativeset.data.service';
import { isValidEmail } from '../utils/email';

@Injectable({ providedIn: 'root' })
export class FollowCreativeSetEffects {
    private actions$ = inject(Actions);
    private followCreativeSetDataService = inject(FollowCreativeSetDataService);

    loadIsFollower$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(loadActions.load),
            switchMap(action => {
                return this.followCreativeSetDataService.getIsFollower(action.creativeSetId).pipe(
                    map(data => loadActions.loadSuccess(data)),
                    catchError(error => of(loadActions.loadFailure({ error })))
                );
            })
        );
    });

    putFollow$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(followActions.follow),
            switchMap(({ creativeSetId }) => {
                return this.followCreativeSetDataService.putFollow(creativeSetId).pipe(
                    map(() => followActions.followSuccess()),
                    catchError(error => of(followActions.followFailure({ error })))
                );
            })
        );
    });

    putUnfollow$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(unfollowActions.unfollow),
            switchMap(({ creativeSetId }) => {
                return this.followCreativeSetDataService.putUnfollow(creativeSetId).pipe(
                    map(() => unfollowActions.unfollowSuccess()),
                    catchError(error => of(unfollowActions.unfollowFailure({ error })))
                );
            })
        );
    });

    initShowcaseState$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(showcaseLoadActions.init),
            switchMap(({ creativeSetId }) => {
                const { userEmail, userName } = this.getUserDataFromLocalStorage();
                if (userEmail && userName) {
                    return of(showcaseLoadActions.initSuccess({ creativeSetId, userEmail, userName }));
                }
                return EMPTY;
            })
        );
    });

    initShowcaseStateSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(showcaseLoadActions.initSuccess),
            switchMap(({ creativeSetId, userEmail }) => {
                return of(showcaseLoadActions.load({ creativeSetId, userEmail }));
            })
        );
    });

    loadShowcaseIsFollower$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(showcaseLoadActions.load),
            switchMap(({ userEmail, creativeSetId }) => {
                return this.followCreativeSetDataService
                    .getShowcaseIsFollower(creativeSetId, userEmail ?? '')
                    .pipe(
                        map(data => loadActions.loadSuccess(data)),
                        catchError(error => of(loadActions.loadFailure({ error })))
                    );
            })
        );
    });

    putShowcaseFollow$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(showcaseFollowActions.follow),
            switchMap(({ creativeSetId, userEmail, userName }) => {
                return this.followCreativeSetDataService
                    .putShowcaseFollow(creativeSetId, userEmail, userName)
                    .pipe(
                        map(() => showcaseFollowActions.followSuccess()),
                        catchError(error => of(showcaseFollowActions.followFailure({ error })))
                    );
            })
        );
    });

    putShowcaseUnfollow$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(showcaseUnfollowActions.unfollow),
            switchMap(({ creativeSetId, userEmail }) => {
                return this.followCreativeSetDataService
                    .putShowcaseUnfollow(creativeSetId, userEmail)
                    .pipe(
                        map(() => showcaseUnfollowActions.unfollowSuccess()),
                        catchError(error => of(showcaseUnfollowActions.unfollowFailure({ error })))
                    );
            })
        );
    });

    // CommentService stores this data in localStorage
    private getUserDataFromLocalStorage(): {
        userEmail: string | undefined;
        userName: string | undefined;
    } {
        try {
            const data = localStorage.getItem('csExternalUser');
            const { username, email } = JSON.parse(data ?? '{}');
            if (!isValidEmail(email)) {
                return { userEmail: undefined, userName: undefined };
            }
            return { userEmail: email, userName: username };
        } catch {
            return { userEmail: undefined, userName: undefined };
        }
    }
}
